import type LatLng from './LatLng';

export type UserLocation = LatLng & {
  city: string;
  city_id: number;
  country: number;
  country_name?: string;
  region: number | string;
  regionName: string;
};

export type User = {
  firstName: string;
  lastName: string;
  id: string;
  slug: string;
  profilePhoto?: {
    id?: string | number;
    href: string;
  };
  location: UserLocation;
  links?: {
    rel: string;
    href: string;
  }[];
};

export type UserConnectionStatus =
  | 'following'
  | 'follow-requested'
  | 'follow-requested-by'
  | 'followed-by'
  | 'blocking'
  | 'blocked-by'
  | 'suggested-follow'
  | 'not-connected'
  | 'contact-of'
  | 'mutual';

export enum UserConnectionLinkRel {
  RequestFollow = 'urn:alltrails:connect:request-follow',
  RequestFollowBack = 'urn:alltrails:connect:request-follow-back',
  RetractRequest = 'urn:alltrails:connect:retract-request',
  Unfollow = 'urn:alltrails:connect:unfollow',
  Dismiss = 'urn:alltrails:connect:dismiss',
  Block = 'urn:alltrails:connect:block',
  Unblock = 'urn:alltrails:connect:unblock',
  Accept = 'urn:alltrails:connect:accept-request',
  Deny = 'urn:alltrails:connect:deny-request',
  RemoveFollower = 'urn:alltrails:connect:remove-follower'
}

export type UserConnectionLink = {
  href: string;
  method: RequestInit['method']; // seems to always be PATCH?
  rel: UserConnectionLinkRel;
  title?: string;
};

export enum UserSuggestionType {
  LocalContributor = 'local-contributor',
  MutualFriends = 'mutual-friends',
  Location = 'location',
  FacebookGraph = 'facebook-graph',
  ContactBook = 'contact-book'
}

export type UserSuggestionReason = {
  label: string;
  score: number;
  type: UserSuggestionType;
};

export type UserSuggestionContext = {
  extraReasons: UserSuggestionReason[];
  primaryReason: UserSuggestionReason;
};

export type BelayUserConnection = {
  links: UserConnectionLink[];
  status: UserConnectionStatus[];
  user: User;
};

export enum RequestState {
  Idle = 'idle',
  Loading = 'loading',
  Succeeded = 'succeeded',
  Failed = 'failed',
  Unauthorized = 'unauthorized'
}

export type ErrorState = 'Retry' | 'NotAvailable';

export type Action = {
  href: string;
  method: RequestInit['method'];
  requestState: string; // TODO: use RequestState once out of the monorepo
};

export enum UserConnectionActionType {
  Follow = 'follow',
  FollowBack = 'followBack',
  RetractRequest = 'retractRequest',
  Unfollow = 'unfollow',
  Dismiss = 'dismiss',
  Block = 'block',
  Unblock = 'unblock',
  Accept = 'accept',
  Deny = 'deny',
  Invite = 'invite',
  RemoveFollower = 'removeFollower'
}

export type UserConnectionActions = Partial<Record<UserConnectionActionType, Action>>;

// Client-side type for BelayUserConnection, which matches the typing from the server (actions vs links)
export type UserConnection = {
  user: User;
  status: UserConnectionStatus[];
  actions: UserConnectionActions;
  suggestionContext?: UserSuggestionContext;
};

export type PaginationLink = {
  rel: 'start' | 'next' | 'prev';
  href: string;
};

export type PageInfo = {
  hasNextPage: boolean;
  itemCount: number;
  totalItemCount: number;
  nextCursor?: string;
  prevCursor?: string;
};

export type ConnectionPageResponse = {
  connections: BelayUserConnection[];
  links: PaginationLink[];
  pageInfo: PageInfo;
};

export enum UserConnectionListKey {
  FindFriends = 'findFriends',
  FollowerRequests = 'followerRequests',
  Suggested = 'suggested',
  Reactors = 'reactors',
  Blocked = 'blocked',
  Followers = 'followed-by',
  Following = 'following',
  Mutual = 'mutual',
  TrailReview = 'trailReview',
  Notifications = 'notifications',
  Feed = 'feed',
  Collaborators = 'collaborators',
  RecordingUsers = 'recordingUsers'
}
